import React, { useRef, useState } from "react";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, LayoutTemplate } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const ACCEPT = ".xml,.mpp,.xlsx,.xlsm,.csv,.txt,.tsv";

const Fact = ({ label, value, testId }) => (
  <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="mt-0.5 font-display text-sm font-semibold text-slate-900">{value ?? "—"}</div>
  </div>
);

/**
 * Import a plan from MS Project XML, .mpp, Excel or CSV.
 * `projectId` enables "import into this project"; template saving is always offered.
 */
export const PlanImport = ({ open, onOpenChange, projectId, onImported, defaultName = "" }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(true);
  const [tpl, setTpl] = useState({ name: defaultName, description: "" });
  const fileRef = useRef(null);

  const reset = () => { setPreview(null); setBusy(false); setTpl({ name: defaultName, description: "" }); };

  const choose = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/plan-import/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setTpl((t) => ({ ...t, name: t.name || file.name.replace(/\.[^.]+$/, "") }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "That plan could not be read");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const importToProject = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/schedule/import`, {
        tasks: preview.tasks, replace_existing: replace,
      });
      toast.success(`${data.created} tasks imported — hierarchy and links preserved`);
      onOpenChange(false);
      reset();
      onImported?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!tpl.name.trim()) return toast.error("Give the template a name");
    setBusy(true);
    try {
      const { data } = await api.post("/plan-import/template", {
        name: tpl.name.trim(), description: tpl.description || null, tasks: preview.tasks,
      });
      toast.success(`"${data.name}" saved to the template library with ${data.task_count} tasks`);
      onOpenChange(false);
      reset();
      onImported?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="plan-import-dialog" className="max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import a plan</DialogTitle>
          <DialogDescription>
            MS Project XML (.xml), MS Project (.mpp), Excel (.xlsx) or CSV. Task hierarchy, durations,
            predecessor links, resources and progress all come across. Nothing is saved until you confirm.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <label
              data-testid="plan-import-dropzone"
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-6 py-12 text-center transition-colors hover:border-[color:var(--brand-primary)] hover:bg-slate-50"
            >
              <Upload size={22} className="text-slate-400" />
              <span className="text-sm font-semibold text-slate-700">Choose a plan file</span>
              <span className="text-xs text-slate-400">{ACCEPT.replace(/\./g, "").toUpperCase()} · up to 12 MB</span>
              <input ref={fileRef} data-testid="plan-import-file-input" type="file" accept={ACCEPT} className="hidden" onChange={choose} />
            </label>
            {busy && <p className="text-center text-sm text-slate-500">Reading the plan…</p>}
            <p className="text-xs text-slate-500">
              In MS Project use <span className="font-semibold">File → Save As → XML</span> for the most
              faithful import. .mpp works where a Java runtime is available; if it is not, you will be
              told to use XML instead.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="plan-import-source">
              <CheckCircle2 size={15} /> Read {preview.task_count} tasks from {preview.filename} ({preview.source})
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Fact testId="import-fact-tasks" label="Tasks" value={preview.task_count} />
              <Fact testId="import-fact-milestones" label="Milestones" value={preview.milestone_count} />
              <Fact testId="import-fact-links" label="Links" value={preview.link_count} />
              <Fact testId="import-fact-levels" label="Levels" value={preview.max_level} />
              <Fact testId="import-fact-start" label="Start" value={preview.start_date} />
              <Fact testId="import-fact-finish" label="Finish" value={preview.finish_date} />
            </div>

            {preview.columns?.detected && (
              <div className="rounded border border-slate-200 p-3" data-testid="import-column-map">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Columns matched</div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                  {Object.entries(preview.columns.detected).map(([field, header]) => (
                    <span key={field}><span className="text-slate-400">{field}</span> → {header}</span>
                  ))}
                </div>
              </div>
            )}

            {preview.warnings?.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="import-warnings">
                <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={13} /> {preview.warnings.length} thing(s) to check</div>
                <ul className="mt-1 list-disc pl-4">{preview.warnings.slice(0, 6).map((w) => <li key={w}>{w}</li>)}</ul>
              </div>
            )}

            <div className="max-h-64 overflow-auto rounded border border-slate-200" data-testid="import-preview-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>{["Task", "Start", "Finish", "Days", "Predecessors", "Resource", "%"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.tasks.map((t, i) => (
                    <tr key={i} data-testid={`import-preview-row-${i}`}>
                      <td className="px-2 py-1.5" style={{ paddingLeft: `${8 + (t.outline_level - 1) * 14}px` }}>
                        {t.is_milestone && <span className="mr-1 text-amber-500">◆</span>}{t.name}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">{t.start || "—"}</td>
                      <td className="px-2 py-1.5 text-slate-500">{t.finish || "—"}</td>
                      <td className="px-2 py-1.5 text-slate-500">{t.duration_days ?? "—"}</td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {(t.predecessors || []).map((p) => `${p.uid}${p.type}${p.lag_days ? (p.lag_days > 0 ? `+${p.lag_days}` : p.lag_days) : ""}`).join(", ") || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">{t.resources || "—"}</td>
                      <td className="px-2 py-1.5 text-slate-500">{t.percent_complete || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
              <div>
                <label className="field-label">Save to the template library as</label>
                <Input data-testid="import-template-name-input" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} placeholder="e.g. Client master plan" />
              </div>
              <div>
                <label className="field-label">Description</label>
                <Textarea data-testid="import-template-description-input" rows={1} value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })} />
              </div>
            </div>

            {projectId && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input data-testid="import-replace-checkbox" type="checkbox" className="h-4 w-4" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
                Replace the tasks currently on this project
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" data-testid="plan-import-cancel-btn" onClick={() => { onOpenChange(false); reset(); }}>Cancel</Button>
          {preview && (
            <>
              <Button variant="outline" data-testid="plan-import-template-btn" disabled={busy} onClick={saveAsTemplate} className="gap-1.5">
                <LayoutTemplate size={14} /> Save as template
              </Button>
              {projectId && (
                <Button data-testid="plan-import-commit-btn" disabled={busy} onClick={importToProject} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
                  <FileSpreadsheet size={14} /> Import into this project
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
